@value mobile, tablet, laptop, desktop from "@shared/styles/breakpoints.module.css";

/* Прозрачность 0.01 сохраняет героя кандидатом LCP с первого кадра. */
@keyframes fadeUp {
  from {
    opacity: 0.01;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Конечная прозрачность берётся из стилей изображения. */
@keyframes fadeIn {
  from {
    opacity: 0.01;
  }
}

@keyframes slideInRight {
  from {
    opacity: 0.01;
    transform: translateX(24px);
  }
}

.root {
  width: 100%;
  max-width: var(--layout-content-max);
  margin: 0 auto;
}

.heroSection {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  display: flex;
  align-items: center;
  min-height: clamp(480px, 60vh, 700px);
  padding: clamp(var(--space-48), 7vw, var(--space-96)) var(--space-page);
  /* без нижней линии: секции главной разделены воздухом, конец первого экрана читается и так */
  background: var(--color-bg);

  /* на десктопе overlay z1 — позади картинки (z2), текстовая зона непрозрачна */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    background: linear-gradient(
      to right,
      var(--color-bg) 0%,
      color-mix(in srgb, var(--color-bg) 85%, transparent) 40%,
      transparent 70%
    );
  }

  /* На узком ПК, планшете и телефоне продукт встаёт под текст в полную силу,
     а не водяным знаком за ним — overlay больше не нужен */
  @media (max-width: desktop) {
    flex-direction: column;
    align-items: stretch;
    min-height: unset;

    &::after {
      display: none;
    }
  }

  @media (max-width: mobile) {
    padding: clamp(var(--space-44), 10vw, var(--space-64))
      clamp(var(--space-20), 5vw, var(--space-32)) clamp(var(--space-40), 8vw, var(--space-56));
  }
}

.heroContent {
  position: relative;
  z-index: 3;
  max-width: var(--layout-hero-content-size);

  @media (max-width: desktop) {
    max-width: 100%;
  }
}

.heroTitle {
  font-family: var(--font-heading);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  line-height: 1.06;
  margin-bottom: clamp(var(--space-16), 2vw, var(--space-28));
  color: var(--color-text);
  font-size: var(--type-hero);
  text-wrap: balance;

  animation: fadeUp var(--motion-enter) var(--motion-ease-enter) both;
  animation-delay: calc(var(--motion-stagger) * 0);

  em {
    font-style: normal;
    color: var(--color-text);
  }

  @media (max-width: mobile) {
    letter-spacing: 0.5px;
  }
}

.heroDesc {
  font-size: var(--type-body);
  line-height: 1.65;
  color: var(--color-text-muted);
  max-width: 520px;
  margin-bottom: clamp(var(--space-24), 3vw, var(--space-40));

  animation: fadeUp var(--motion-enter) var(--motion-ease-enter) both;
  animation-delay: calc(var(--motion-stagger) * 1);

  @media (max-width: mobile) {
    max-width: 100%;
  }
}

.heroActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-12);
  align-items: center;

  animation: fadeUp var(--motion-enter) var(--motion-ease-enter) both;
  animation-delay: calc(var(--motion-stagger) * 2);

  @media (max-width: mobile) {
    flex-direction: row;
  }
}

.ctaButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-10);
  padding: clamp(var(--space-10), 1.2vw, var(--space-14))
    clamp(var(--space-20), 2.5vw, var(--space-32));
  background: var(--color-primary);
  color: var(--color-cta-text-color);
  border: none;
  border-radius: var(--radius-control);
  min-height: var(--layout-action-height);
  cursor: pointer;
  font-family: var(--font-body);
  font-size: clamp(0.875rem, 0.95vw, 0.9375rem);
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  transition:
    filter var(--motion-fast) ease,
    transform var(--motion-fast) var(--motion-ease-out);

  /* Кнопка открывает меню каталога — шеврон честно это обещает и поворачивается при открытии */
  svg {
    width: 18px;
    height: 18px;
    transition: transform var(--motion-fast) var(--motion-ease-out);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      filter: brightness(1.08);
    }
  }

  /* Нажатие — короткий отклик вместо подъёма при наведении */
  &:active {
    transform: scale(0.97);
  }

  @media (max-width: mobile) {
    justify-content: center;
    padding: var(--space-15) var(--space-24);
  }
}

.ctaLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-10);
  padding: clamp(var(--space-10), 1.2vw, var(--space-14))
    clamp(var(--space-20), 2.5vw, var(--space-32));
  border: 1px solid var(--color-text-muted);
  border-radius: var(--radius-control);
  min-height: var(--layout-action-height);
  color: var(--color-text);
  text-decoration: none;
  font-family: var(--font-body);
  font-size: clamp(0.875rem, 0.95vw, 0.9375rem);
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  transition:
    border-color var(--motion-fast) ease,
    color var(--motion-fast) ease,
    transform var(--motion-fast) var(--motion-ease-out);

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      border-color: var(--color-primary);
      color: var(--color-primary);
    }
  }

  &:active {
    transform: scale(0.97);
  }

  @media (max-width: mobile) {
    padding: var(--space-15) var(--space-24);
  }
}

.heroSection .heroImage {
  position: absolute;
  right: var(--space-page);
  top: 0;
  bottom: 0;
  margin: auto 0;
  height: auto;
  /* Сохраняем крупную иллюстрацию, оставляя тексту собственное место. */
  width: min(
    var(--layout-hero-image-size),
    calc(100% - var(--layout-hero-content-size) - 2 * var(--space-page) - var(--space-24))
  );
  pointer-events: none;
  z-index: 2;
  opacity: 1;
  /* продукт въезжает чуть дольше текста и с шагом задержки — фон каскада, а не его начало */
  animation: slideInRight calc(var(--motion-enter) * 1.25) var(--motion-ease-enter)
    var(--motion-stagger) both;

  @media (max-width: desktop) {
    position: static;
    order: 2;
    align-self: center;
    width: min(100%, 520px);
    height: auto;
    margin: clamp(var(--space-32), 5vw, var(--space-48)) 0 0;
    animation: fadeIn var(--motion-enter) ease calc(var(--motion-stagger) * 3) both;
  }
}

.aboutSection {
  display: grid;
  gap: clamp(var(--space-20), 2.5vw, var(--space-36));
  /* Ритм секций главной: одинаковый воздух до заголовка — 64 px на телефоне, 96 px на десктопе */
  padding: clamp(var(--space-64), 7vw, var(--space-96)) var(--space-page) 0;
  background: var(--color-bg);
}

.aboutBody {
  display: grid;
  grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
  gap: clamp(var(--space-28), 4.5vw, var(--space-80));

  @media (max-width: laptop) {
    grid-template-columns: 1fr;
  }
}

.aboutText {
  display: grid;
  gap: var(--space-16);
  max-width: 640px;
  font-size: var(--type-body);
  line-height: 1.65;
  color: var(--color-text);

  p + p {
    color: var(--color-text-muted);
  }
}

.aboutPoints {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(var(--space-24), 2.5vw, var(--space-32)) clamp(var(--space-20), 2.5vw, var(--space-36));
  align-content: start;
  margin: 0;
  padding: 0;
  list-style: none;

  @media (max-width: mobile) {
    grid-template-columns: 1fr;
  }
}

.aboutPointTitle {
  font-family: var(--font-heading);
  font-size: var(--type-catalog);
  font-weight: 700;
  letter-spacing: normal;
  text-transform: none;
  color: var(--color-text);
}

.aboutPointText {
  margin-top: var(--space-6);
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--color-text-muted);
}

.customersSection {
  padding: clamp(var(--space-64), 7vw, var(--space-96)) var(--space-page)
    clamp(var(--space-64), 7vw, var(--space-96));
  background: var(--color-bg);
}

.sectionHeader {
  display: grid;
  gap: var(--space-10);
  margin-bottom: clamp(var(--space-20), 2.5vw, var(--space-32));
}

.sectionTitle {
  font-family: var(--font-heading);
  font-size: var(--type-section);
  font-weight: 600;
  letter-spacing: normal;
  line-height: 1.1;
  text-transform: none;
  color: var(--color-text);
}

.sectionMeta {
  font-size: clamp(0.875rem, 1vw, 1rem);
  color: var(--color-text-muted);
}

.ctaButton[aria-expanded="true"] svg {
  transform: rotate(180deg);
}
